import { useMsal } from "@azure/msal-react";
import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import { useGetMemberships } from "../../api/queries/useGetMemberships";
import { useAppContext } from "../../Context/AppContext";
import "../css/RegisterForm.css";
import CheckoutStep from "./RegistrationsSteps/CheckoutStep";
import ConfirmationStep from "./RegistrationsSteps/ConfirmationStep";
import MembershipStep from "./RegistrationsSteps/MemberShipStep";
import PersonalInfoStep from "./RegistrationsSteps/PersonalInfoStep";
import TermsStep from "./RegistrationsSteps/TermsStep";
import StepsProgressBar from "./StepsProgressBar";

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  city: string;
  personalPositionCompany: string;
  businessName: string;
  isEmailUpdates: number;
  isGDPRConsent: number;
  membershipName: string;
  isAnnualMembership: number;
  isTermConditions: number;
  isDirectory: number;
  billingAddress: string;
  paymentMethodId: string;
  referralCodeUsed?: string;
}

const RegisterUser = () => {
  const { instance, accounts } = useMsal();
  const { data: memberships } = useGetMemberships();
  const [membershipPrice, setMembershipPrice] = useState<number>(0);
  const [step, setStep] = useState(0);
  const [userData, setUserData] = useState<Record<string, any>>({});
  const [discountCode, setDiscountCode] = useState("");
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    city: "",
    personalPositionCompany: "",
    businessName: "",
    isEmailUpdates: 0,
    isGDPRConsent: 0,
    membershipName: "Youth Membership",
    isAnnualMembership: 0,
    isTermConditions: 0,
    isDirectory: 0,
    billingAddress: "",
    paymentMethodId: "",
  });

  const { objectId } = useAppContext();
  const REGISTRATION_CONFIRMATION_KEY = "registration_show_confirmation";

  useEffect(() => {
    const storedData = localStorage.getItem("UserData");
    const mergedData = storedData
      ? { ...JSON.parse(storedData), ...userData }
      : userData;
    setUserData(mergedData);
    if (storedData) {
      setUserData(JSON.parse(storedData));
    }
  }, []);

  // If user just completed checkout and component remounted (e.g. after refetch), show Jazakallah step
  useEffect(() => {
    if (sessionStorage.getItem(REGISTRATION_CONFIRMATION_KEY) === "1") {
      sessionStorage.removeItem(REGISTRATION_CONFIRMATION_KEY);
      setStep(4);
    }
  }, []);

  const stepLabels = ["Personal Info", "Membership", "Terms", "Checkout", "Confirmation"];

  const nextStep = () => {
    setStep((s) => s + 1);
  };
  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  const handlePersonalInfoNext = (data: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    nextStep();
  };

  const handleMembershipNext = (data: Partial<FormData>, price: number) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setMembershipPrice(price);
    nextStep();
  };

  const handleTermsNext = (data: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    nextStep();
  };

  useEffect(() => {
    localStorage.setItem(
      "UserData",
      JSON.stringify({
        ...formData,
        objectId: objectId || "",
        discountCode: discountCode,
      })
    );
  }, [formData]);

  const handleCheckoutNext = () => {
    nextStep();
  };

  const getAccessToken = async () => {
    const request = {
      scopes: ["https://smbfglobal.onmicrosoft.com/user-api/user.read"],
      account: accounts[0],
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      const decoded: any = jwtDecode(response.accessToken);

      setFormData((prev) => ({
        ...prev,
        firstName: decoded.given_name || "",
        lastName: decoded.family_name || "",
        email: decoded.emails?.[0] || "",
        isGDPRConsent: 0,
        isEmailUpdates: 0,
      }));
    } catch (err) {
      console.error("Token retrieval failed", err);
    }
  };

  useEffect(() => {
    getAccessToken();
  }, []);

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <PersonalInfoStep
            defaultValues={{
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phoneNumber: formData.phoneNumber,
              city: formData.city,
              personalPositionCompany: formData.personalPositionCompany,
              businessName: formData.businessName,
              // isEmailUpdates: formData.isEmailUpdates,
              // isGDPRConsent: formData.isGDPRConsent,
            }}
            onNext={handlePersonalInfoNext}
          />
        );
      case 1:
        return (
          <MembershipStep
            discountCode={discountCode}
            setDiscountCode={setDiscountCode}
            defaultValues={{
              membershipName: formData.membershipName,
              isAnnualMembership: formData.isAnnualMembership,
            }}
            memberships={
              Array.isArray(memberships)
                ? memberships
                : memberships
                ? [memberships]
                : []
            }
            setMembershipPrice={setMembershipPrice}
            onNext={handleMembershipNext}
            onBack={prevStep}
          />
        );
      case 2:
        return (
          <TermsStep
            defaultValues={{
              isTermConditions: formData.isTermConditions,
              isDirectory: formData.isDirectory,
            }}
            onNext={handleTermsNext}
            onBack={prevStep}
          />
        );
      case 3:
        return (
          <CheckoutStep
            selectedMembership={formData.membershipName}
            membershipPrice={membershipPrice}
            memberships={
              Array.isArray(memberships)
                ? memberships
                : memberships
                ? [memberships]
                : []
            }
            isAnnualMembership={
              formData.isAnnualMembership === 1 ? "annual" : "monthly"
            }
            onNext={handleCheckoutNext}
            onBack={prevStep}
            discountCode={discountCode}
          />
        );
      case 4:
        return (
          <ConfirmationStep
            membershipPrice={membershipPrice}
            discountCode={discountCode}
            selectedMembership={formData.membershipName}
            isAnnualMembership={
              formData.isAnnualMembership === 1 ? "annual" : "monthly"
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="m-4 p-4 rounded-4 border-0 shadow d-flex flex-column">
      <StepsProgressBar currentStep={step} labels={stepLabels} />
      {renderStep()}
    </div>
  );
};

export default RegisterUser;